/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { toast } from 'sonner';
import i18n from '@/shared/i18n';

interface SharePropertyOptions {
  title: string;
  agencySlug?: string;
  propertySlug?: string;
  propertyId?: string;
}

/**
 * Generates a URL-friendly slug from text, matching the backend logic.
 */
function slugify(text: string): string {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Builds the public client listing URL (`/p/{agency}/{property}`).
 * Generates a slug from the title + id when no slug is provided.
 *
 * @param options - Share context with agency and property slugs
 * @returns Absolute public URL, or null when agency slug is missing
 */
function resolvePublicUrl(options: SharePropertyOptions): string | null {
  const { agencySlug, propertySlug, title, propertyId } = options;
  if (!agencySlug) return null;
  const slug = propertySlug || deriveSlug(title, propertyId);
  if (!slug) return null;
  return `${window.location.origin}/p/${agencySlug}/${slug}`;
}

/**
 * Derives a slug from title and property id when backend slug is absent.
 */
function deriveSlug(title: string, propertyId?: string): string {
  const base = slugify(title) || 'imovel';
  const short = propertyId?.slice(0, 8) || '';
  return short ? `${base}-${short}` : base;
}

/**
 * Attempts the Web Share API; returns true when shared or user-cancelled.
 */
async function tryNativeShare(title: string, url: string): Promise<boolean> {
  if (!navigator.share) return false;
  try {
    await navigator.share({ title, url });
    return true;
  } catch (err: unknown) {
    const name = err instanceof DOMException ? err.name : (err as Error)?.name;
    return name === 'AbortError';
  }
}

/**
 * Copies the URL to the clipboard, toasting success or failure.
 * Falls back to execCommand for insecure contexts (HTTP).
 */
async function copyToClipboard(url: string): Promise<void> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    toast.success(i18n.t('features/properties:detailView.share.linkCopied'));
  } catch {
    toast.error(i18n.t('features/properties:detailView.share.copyFailed'));
  }
}

/**
 * Copies the public property link for clients (no login).
 * Reuses the same `/p/:agency_slug/:property_slug` route as Meu Site.
 *
 * @param options - Property title and public slugs
 */
export async function shareProperty(options: SharePropertyOptions): Promise<void> {
  const publicUrl = resolvePublicUrl(options);
  const url = publicUrl || window.location.href;
  const shared = await tryNativeShare(options.title, url);
  if (shared) return;
  await copyToClipboard(url);
}

/**
 * Notifies the backend that a share event occurred for a property.
 * Fire-and-forget: failures are silently ignored.
 */
export async function trackShareEvent(agencyId: string, propertyId: string): Promise<void> {
  try {
    const { request: __request } = await import('@/services/requests/core/request');
    const { OpenAPIHousien } = await import('@/services/requests/core/OpenAPI');
    await __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agencies/{agencyId}/properties/{propertyId}/events',
      path: { agencyId, propertyId },
      body: { event_type: 'share' },
      mediaType: 'application/json',
    });
  } catch {
    /* fire-and-forget */
  }
}
